import { useState } from "react";
import { motion } from "framer-motion";
import {
  EnvelopeSimple,
  User,
  ChatText,
  PaperPlaneTilt,
  CircleNotch,
  CheckCircle,
  Flask,
  Clock,
  ShieldCheck,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const SUBJECTS = [
  "Order / shipping issue",
  "Product / COA question",
  "Payment / refund",
  "Wholesale enquiry",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      setSent(true);
      toast.success(data.message || "Message sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-cyan/15 bg-navy py-3 px-4 font-mono text-sm text-white placeholder:text-teal/40 outline-none transition-colors focus:border-cyan/50";
  const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-teal";

  return (
    <div data-testid="contact-page" className="mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-12">
      <div className="border-b border-cyan/15 pb-8">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-cyan">// Support</span>
        <h1 className="mt-3 font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl lg:text-5xl">
          Contact our team
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-teal-muted">
          Questions about an order, a Certificate of Analysis, or a product? Send us a message and
          our research-support team will get back to you.
        </p>
      </div>

      <div className="grid gap-12 py-12 lg:grid-cols-[1fr_340px]">
        {/* Form / Success */}
        <div>
          {sent ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="contact-success"
              className="border border-cyan/25 bg-navy-surface p-10 text-center shadow-glow-sm"
            >
              <CheckCircle size={56} weight="fill" className="mx-auto text-cyan text-glow" />
              <h2 className="mt-5 font-heading text-2xl font-bold text-white">Message received</h2>
              <p className="mt-3 text-sm text-teal-muted">
                Thanks, {form.name.split(" ")[0] || "there"} — our team will reply to{" "}
                <span className="text-cyan">{form.email}</span> shortly.
              </p>
              <button
                data-testid="contact-send-another"
                onClick={() => {
                  setSent(false);
                  setForm({ name: "", email: "", subject: SUBJECTS[0], message: "" });
                }}
                className="mt-7 border border-cyan/30 px-6 py-2.5 font-mono text-xs uppercase tracking-wider text-cyan transition-colors hover:bg-cyan/10"
              >
                Send another
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-5 border border-cyan/15 bg-navy-surface p-7 sm:p-9">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Name / Institution</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-teal" />
                    <input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Dr. Jane Researcher" className={`${inputCls} pl-9`} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <div className="relative">
                    <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-teal" />
                    <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="you@lab.com" className={`${inputCls} pl-9`} />
                  </div>
                </div>
              </div>

              <div>
                <label className={labelCls}>Subject</label>
                <select data-testid="contact-subject" value={form.subject} onChange={set("subject")} className={inputCls}>
                  {SUBJECTS.map((s) => (
                    <option key={s} value={s} className="bg-navy">{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Message</label>
                <div className="relative">
                  <ChatText size={16} className="absolute left-3 top-4 text-teal" />
                  <textarea data-testid="contact-message" rows={6} value={form.message} onChange={set("message")} placeholder="How can we help?" className={`${inputCls} resize-none pl-9`} />
                </div>
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="flex items-center justify-center gap-2 bg-cyan px-8 py-3.5 font-mono text-sm font-semibold uppercase tracking-wider text-navy-deep transition-all duration-300 hover:bg-cyan-bright hover:shadow-glow disabled:opacity-60"
              >
                {loading ? <CircleNotch size={18} className="animate-spin" /> : <><PaperPlaneTilt size={16} weight="fill" /> Send Message</>}
              </button>
            </form>
          )}
        </div>

        {/* Support info */}
        <aside className="space-y-6">
          <div className="border border-cyan/15 bg-navy-surface p-6">
            <h3 className="font-mono text-xs uppercase tracking-widest text-cyan">// Direct</h3>
            <a href="mailto:support@quadhelixlabs.com" className="mt-4 flex items-center gap-3 text-sm text-white transition-colors hover:text-cyan">
              <EnvelopeSimple size={18} className="text-cyan" /> support@quadhelixlabs.com
            </a>
            <div className="mt-3 flex items-center gap-3 text-sm text-teal-muted">
              <Clock size={18} className="text-cyan" /> Mon–Fri · replies within 24h
            </div>
          </div>

          <div className="border border-cyan/15 bg-navy-surface p-6">
            <h3 className="font-mono text-xs uppercase tracking-widest text-cyan">// Good to know</h3>
            <ul className="mt-4 space-y-3 text-sm text-teal-muted">
              <li className="flex items-start gap-3"><ShieldCheck size={18} weight="fill" className="mt-0.5 shrink-0 text-cyan" /> Every batch ships with a Certificate of Analysis — ask us for a copy anytime.</li>
              <li className="flex items-start gap-3"><Flask size={18} weight="fill" className="mt-0.5 shrink-0 text-cyan" /> All products are strictly for laboratory research use only.</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
